import {
    CheckCircleIcon,
    PlusIcon,
    TagIcon,
    UsersIcon,
    XCircleIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { format } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';

/**
 * Promotional codes.
 *
 * Two kinds: a trial code puts someone on a plan for a number of months at
 * no cost, and a percentage code reduces the price at checkout. Trials are
 * how agents get onto the platform before deciding whether to pay.
 */

const emptyForm = {
  code: '',
  description: '',
  type: 'trial',
  planName: 'agent',
  trialMonths: 3,
  discountPercent: 20,
  maxRedemptions: 50,
  oncePerUser: true,
  validUntil: '',
  isPublic: true,
};

const CouponManagement = () => {
  const [coupons, setCoupons] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [redemptionsFor, setRedemptionsFor] = useState(null);
  const [redemptions, setRedemptions] = useState([]);

  const auth = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` },
  });

  const base = process.env.REACT_APP_API_URL;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [c, p] = await Promise.all([
        axios.get(`${base}/api/coupons`, auth()),
        axios.get(`${base}/api/admin/membership-plans`, auth()),
      ]);
      setCoupons(c.data?.data?.coupons || []);
      setPlans(Array.isArray(p.data?.data) ? p.data.data : []);
    } catch (error) {
      toast.error('Failed to load codes');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const create = async () => {
    if (!form.code.trim()) {
      toast.error('Enter a code');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        description: form.description.trim(),
        type: form.type,
        maxRedemptions: form.maxRedemptions ? Number(form.maxRedemptions) : undefined,
        oncePerUser: form.oncePerUser,
        validUntil: form.validUntil || undefined,
        isPublic: form.isPublic,
      };

      if (form.type === 'trial') {
        payload.planName = form.planName;
        payload.trialMonths = Number(form.trialMonths);
      } else {
        payload.discountPercent = Number(form.discountPercent);
      }

      await axios.post(`${base}/api/coupons`, payload, auth());
      toast.success('Code created');
      setForm(emptyForm);
      setShowForm(false);
      load();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not create the code');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (coupon) => {
    try {
      await axios.patch(
        `${base}/api/coupons/${coupon._id}`,
        { isActive: !coupon.isActive },
        auth()
      );
      toast.success(coupon.isActive ? 'Code disabled' : 'Code enabled');
      load();
    } catch (error) {
      toast.error('Could not update the code');
    }
  };

  const viewRedemptions = async (coupon) => {
    setRedemptionsFor(coupon);
    try {
      const res = await axios.get(`${base}/api/coupons/${coupon._id}/redemptions`, auth());
      setRedemptions(res.data?.data?.redemptions || []);
    } catch (error) {
      setRedemptions([]);
    }
  };

  const planLabel = (name) => {
    const p = plans.find((x) => x.name === name);
    return p ? (p.displayName || p.name) : name;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500" />
      </div>
    );
  }

  const active = coupons.filter((c) => c.isActive).length;
  const totalRedemptions = coupons.reduce((s, c) => s + (c.redemptionCount || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Promotional Codes</h1>
          <p className="text-gray-600 mt-1">
            Trial codes let an agent use a plan before paying for it
          </p>
        </div>

        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          className="flex items-center px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-semibold"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          {showForm ? 'Cancel' : 'New code'}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Codes</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{coupons.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Active</p>
          <p className="text-2xl font-bold text-green-600 mt-1">{active}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Times used</p>
          <p className="text-2xl font-bold text-orange-600 mt-1">{totalRedemptions}</p>
        </div>
      </div>

      <AnimatePresence>
        {showForm ? (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-white rounded-xl border border-gray-200 overflow-hidden"
          >
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">New code</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="text-sm text-gray-700">
                  Code
                  <input
                    type="text"
                    value={form.code}
                    onChange={(e) => set('code', e.target.value.toUpperCase())}
                    placeholder="AGENT3FREE"
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </label>

                <label className="text-sm text-gray-700">
                  Type
                  <select
                    value={form.type}
                    onChange={(e) => set('type', e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="trial">Free trial of a plan</option>
                    <option value="percent">Percentage discount</option>
                  </select>
                </label>

                {form.type === 'trial' ? (
                  <>
                    <label className="text-sm text-gray-700">
                      Plan granted
                      <select
                        value={form.planName}
                        onChange={(e) => set('planName', e.target.value)}
                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        {plans.map((p) => (
                          <option key={p._id} value={p.name}>
                            {p.displayName || p.name}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="text-sm text-gray-700">
                      Months
                      <input
                        type="number"
                        min="1"
                        max="24"
                        value={form.trialMonths}
                        onChange={(e) => set('trialMonths', e.target.value)}
                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </label>
                  </>
                ) : (
                  <label className="text-sm text-gray-700">
                    Discount (%)
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={form.discountPercent}
                      onChange={(e) => set('discountPercent', e.target.value)}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </label>
                )}

                <label className="text-sm text-gray-700">
                  Maximum uses
                  <input
                    type="number"
                    min="1"
                    value={form.maxRedemptions}
                    onChange={(e) => set('maxRedemptions', e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-xs text-gray-500 mt-1 block">
                    Leave a sensible cap — a code shared around spreads quickly
                  </span>
                </label>

                <label className="text-sm text-gray-700">
                  Valid until (optional)
                  <input
                    type="date"
                    value={form.validUntil}
                    onChange={(e) => set('validUntil', e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </label>

                <label className="text-sm text-gray-700 md:col-span-2">
                  Description
                  <input
                    type="text"
                    value={form.description}
                    onChange={(e) => set('description', e.target.value)}
                    placeholder="What this code is for"
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </label>
              </div>

              <div className="flex flex-wrap gap-5 mt-4">
                <label className="flex items-center text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.oncePerUser}
                    onChange={(e) => set('oncePerUser', e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 mr-2"
                  />
                  One use per account
                </label>

                <label className="flex items-center text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.isPublic}
                    onChange={(e) => set('isPublic', e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 mr-2"
                  />
                  Show in the app as an available offer
                </label>
              </div>

              <button
                type="button"
                onClick={create}
                disabled={saving}
                className="mt-5 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-semibold disabled:opacity-60"
              >
                {saving ? 'Creating...' : 'Create code'}
              </button>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center">
          <TagIcon className="w-5 h-5 text-gray-500 mr-2" />
          <h2 className="font-semibold text-gray-900">All codes</h2>
        </div>

        {coupons.length === 0 ? (
          <div className="py-16 text-center text-gray-500">
            No codes yet. Create one to let agents try a plan before paying.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Code</th>
                  <th className="text-left px-5 py-3 font-semibold">Gives</th>
                  <th className="text-right px-5 py-3 font-semibold">Used</th>
                  <th className="text-left px-5 py-3 font-semibold">Expires</th>
                  <th className="text-left px-5 py-3 font-semibold">Status</th>
                  <th className="text-right px-5 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {coupons.map((c) => (
                  <tr key={c._id} className="hover:bg-gray-50">
                    <td className="px-5 py-3">
                      <p className="font-mono font-semibold text-gray-900">{c.code}</p>
                      {c.description ? (
                        <p className="text-xs text-gray-500 mt-0.5">{c.description}</p>
                      ) : null}
                    </td>
                    <td className="px-5 py-3 text-gray-700">
                      {c.type === 'trial'
                        ? `${planLabel(c.planName)} for ${c.trialMonths} month${c.trialMonths === 1 ? '' : 's'}`
                        : `${c.discountPercent}% off`}
                      {c.isPublic ? (
                        <span className="ml-2 px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700">
                          Shown in app
                        </span>
                      ) : null}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {c.redemptionCount || 0}
                      {c.maxRedemptions ? ` / ${c.maxRedemptions}` : ''}
                    </td>
                    <td className="px-5 py-3 text-gray-600">
                      {c.validUntil ? format(new Date(c.validUntil), 'd MMM yyyy') : 'No expiry'}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${
                        c.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {c.isActive ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => viewRedemptions(c)}
                          title="Who has used it"
                          className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                        >
                          <UsersIcon className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleActive(c)}
                          title={c.isActive ? 'Disable' : 'Enable'}
                          className={`p-2 rounded-lg ${
                            c.isActive ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'
                          }`}
                        >
                          {c.isActive
                            ? <XCircleIcon className="w-4 h-4" />
                            : <CheckCircleIcon className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AnimatePresence>
        {redemptionsFor ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
            onClick={() => setRedemptionsFor(null)}
          >
            <motion.div
              initial={{ scale: 0.96 }}
              animate={{ scale: 1 }}
              className="bg-white rounded-xl w-full max-w-lg max-h-[70vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
                <h3 className="font-semibold text-gray-900">
                  Who has used {redemptionsFor.code}
                </h3>
              </div>

              {redemptions.length === 0 ? (
                <p className="px-6 py-10 text-center text-sm text-gray-500">
                  Nobody has used this code yet.
                </p>
              ) : (
                <div className="divide-y divide-gray-100">
                  {redemptions.map((r) => (
                    <div key={r._id} className="px-6 py-3">
                      <p className="text-sm font-medium text-gray-900">
                        {r.user?.firstName} {r.user?.lastName}
                      </p>
                      <p className="text-xs text-gray-500">
                        {r.user?.email}
                        {' \u00B7 '}
                        {format(new Date(r.redeemedAt), 'd MMM yyyy')}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
};

export default CouponManagement;
